const { isArray, isFunction, isObject } = require('@/functions/is');

/**
 * This is taken from angular's own 'annotate' method used by $injector.
 * Modified to just return an array of the parameter names.
 * Googling shows there is no easy way to get the parameters to a function and suggests using this same method
 * taken from Angular: https://stackoverflow.com/a/12108723
 *
 * @param fn
 * @return {*}
 */
function getFunctionParameterNames(fn) {
    let FN_ARGS = /^function\s*[^(]*\(\s*([^)]*)\)/m;
    let FN_ARG_SPLIT = /,/;
    let FN_ARG = /^\s*(_?)(\S+?)\1\s*$/;
    let STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;

    let $inject;
    let fnText;
    let argDecl;

    if (typeof fn === 'function') {
        if (fn.$inject) {
            return fn.$inject;
        }

        $inject = [];
        if (fn.length) {
            fnText = fn.toString().replace(STRIP_COMMENTS, '');
            argDecl = fnText.match(FN_ARGS);
            argDecl[1].split(FN_ARG_SPLIT).forEach(function (arg) {
                arg.replace(FN_ARG, function (all, underscore, name) {
                    $inject.push(name);
                });
            });
        }
        fn.$inject = $inject;

        return $inject;
    }

    return null;
}

/**
 * Get the name of the class for an object.
 *
 * @param {object} obj
 * @returns {boolean|string}
 */
function getObjectClass(obj) {
    return (typeof obj !== 'object' || obj === null) ? false : /(\w+)\(/.exec(obj.constructor.toString())[1];
}

/**
 * Determines if an object is a date object.
 *
 * @param {object} date
 * @returns {boolean}
 */
function isValidDateObject(date) {
    return getObjectClass(date) === 'Date' && date.toString() !== 'Invalid Date';
}

/**
 * Like Angular's extend, but merges two objects recursively.
 * Modifies the original object.
 *
 * The first parameter is the target object to merge into.
 * The value from all additional parameters (objects) will be merged into the first.
 *
 * @param {...object} args
 *
 * @return {object}
 */
let extendDeep = function extendDeep(...args) {
    let dst = args[0];
    args.forEach((obj) => {
        if (obj !== dst) {
            Object.keys(obj).forEach((key) => {
                let value = obj[key];
                if (dst[key] && isObject(dst[key])) {
                    extendDeep(dst[key], value);
                } else if (!dst[key] && isObject(obj[key])) {
                    dst[key] = extendDeep({}, obj[key]);
                } else if (!isFunction(dst[key])) {
                    dst[key] = value;
                }
            });
        }
    });
    return dst;
};

/**
 * JavaScript implementation of PHP's array_combine function
 * http://locutus.io/php/array/array_combine/
 *
 * @param {array} keys
 * @param {array} values
 * @return {object}
 * @throws {Error}
 *
 * Example:
 * arrayCombine([0,1,2], ['kevin','van','zonneveld']) => {0: 'kevin', 1: 'van', 2: 'zonneveld'}
 */
function arrayCombine(keys, values) {
    const newArray = {};
    // input sanitation
    // Only accept arrays or array-like objects
    // Require arrays to have a count
    if (typeof keys !== 'object') {
        throw new Error('Parameter \'keys\' must be an array');
    }
    if (typeof values !== 'object') {
        throw new Error('Parameter \'values\' must be an array');
    }
    if (typeof keys.length !== 'number') {
        throw new Error('Parameter \'keys\' does not have a numeric length property');
    }
    if (typeof values.length !== 'number') {
        throw new Error('Parameter \'values\' does not have a numeric length property');
    }
    if (!keys.length) {
        throw new Error('Parameter \'keys\' appears to be empty');
    }
    // number of elements does not match
    if (keys.length !== values.length) {
        throw new Error('Parameters \'keys\' and \'values\' are not the same length');
    }
    for (let i = 0; i < keys.length; i++) {
        newArray[keys[i]] = values[i];
    }
    return newArray;
}

/**
 * timeoutPromise() does the same thing as setTimeout but returns a promise which resolves after the delay.
 * Useful for promise chaining or awaiting.
 *
 * @param {number} ms Amount of time to wait before resolving, in milliseconds.
 *
 * @return {Promise<any>}
 */
const timeoutPromise = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
});

/**
 * If you have a temperamental promise that might get stuck and never resolve or reject you can use this function
 * to call it. If the promise resolves or rejects by itself within the specified time you'll receive the result, but
 * if it takes longer than the specified time to return anything the promise returned by this function will
 * automatically reject, so you can continue with your life.
 * Of course that means you shouldn't use this for anything vital. It should only be used to wrap a promise that does
 * something that is a "nice to have". This kind of goes against the principles of promises, but this function is a
 * helper to deal with existing broken promises.
 *
 * @param {Promise} promise
 * @param {number} rejectAfterMs
 *
 * @return {Promise}
 */
const waitForPromiseWithTimeout = (promise, rejectAfterMs) => {
    return new Promise((resolve, reject) => {
        // Set up a timeout to automatically reject after the specified amount of time.
        let rejectTimeout = setTimeout(() => {
            reject(new Error('Promise timed out.'));
        }, rejectAfterMs);

        // If the real promise resolves or rejects in time, pass on that result. If it does, clear the timeout
        // to auto-reject.
        promise
            .then(resolve)
            .catch(reject)
            .finally(() => {
                clearTimeout(rejectTimeout);
            });
    });
};

/**
 * Removes items from an object that have a value===null or are an empty array.
 *
 * @param {object|[]} object
 * @param {boolean} recursive
 *
 * @return {object}
 */
const removeNullValues = (object, recursive = false) => {
    for (let key in object) {
        if (!object.hasOwnProperty(key)) {
            continue;
        }

        if (object[key] === null) {
            removeKeyFromObjectOrArray(object, key);
        }

        if (recursive && isObject(object[key])) {
            // Process objects in the object (if recursion is enabled).
            object[key] = removeNullValues(object[key], true);
        }

        if (recursive && isArray(object[key])) {
            // Process arrays in the object (if recursion is enabled).
            object[key] = removeNullValues(object[key], true);
        }

        if (isArray(object[key]) && object[key].length === 0) {
            removeKeyFromObjectOrArray(object, key);
        }
    }

    return object;
};

/**
 * This function will remove the value at the given key/index from an object or an array.
 *
 * When you call `delete` on an array it will remove the value but leave the index in place,
 * whereas if you use `splice` it will remove the value and shift all the other values down.
 * So this function makes removing a value from an array or an object consistent.
 *
 * @param {object} object
 * @param {string|number} key
 */
const removeKeyFromObjectOrArray = (object, key) => {
    if (object instanceof Array) {
        object.splice(key, 1);
    } else {
        delete object[key];
    }
};

module.exports = {
    arrayCombine,
    extendDeep,
    getFunctionParameterNames,
    getObjectClass,
    isValidDateObject,
    removeNullValues,
    timeoutPromise,
    waitForPromiseWithTimeout,
};
